import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';
// Using direct response processing instead of genericApiGet for better error handling

/**
 * Fetch list of variant options with pagination and filtering
 * @param {Object} filters - Filter parameters for list
 * @returns {Promise} List of variant options
 */
export const getVariantOptionList = async (filters = {}) => {
  try {
    // Format filters according to the API structure
    let queryParams = {};
    
    if (filters) {
      // Handle search term
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        queryParams.filters = JSON.stringify({
          limit: filters.limit || 10,
          offset: filters.offset || 0,
          search_attr: filters.search,
          store_id: localStorage.getItem('store_id')
        });
      } else {
        // Handle pagination and sorting
        const filtersObj = {
          limit: filters.limit || 10,
          offset: filters.offset || 0,
          store_id: localStorage.getItem('store_id')
        };
        
        // Add sorting if provided
        if (filters.sort && filters.sort.length > 0) {
          filtersObj.sort = filters.sort.map(item => ({
            colId: item.field,
            sort: item.sort
          }));
        }
        queryParams.filters = JSON.stringify(filtersObj);
      }
    }
    
    const response = await get(`${process.env.EXTENDED_URL}/variant_option`, queryParams);
    // Ensure we're getting the total count from the response
    let processedResponse = variantOptionListGet(response);
    
    // If the API response has a total field but our processed response doesn't have total_count
    if (response?.data?.total && !processedResponse.total) {
      processedResponse.total = response.data.total;
    }
    
    return processedResponse;
  } catch (error) {
    console.error('Error fetching variant options list:', error);
    return {
      success: false,
      message: 'An error occurred while fetching variant options',
      data: []
    };
  }
};

/**
 * Fetch a single variant option by slack
 * @param {string} slack - Variant option slack
 * @returns {Promise} Variant option details
 */
export const getVariantOptionDetails = async (slack) => {
  try {
    const response = await get(`${process.env.EXTENDED_URL}/variant_option/${slack}`);
    
    return variantOptionDetailsGet(response);
  } catch (error) {
    console.error('Error fetching variant option details:', error);
    return {
      success: false,
      message: 'An error occurred while fetching variant option details',
      data: {}
    };
  }
};

/**
 * Create a new variant option
 * @param {Object} variantOptionData - Variant option data
 * @returns {Promise} Created variant option response
 */
export const createVariantOption = async (variantOptionData) => {
  try {
    const apiData = {
      variant_option_name: variantOptionData.label,
      status: variantOptionData.status || 1,
      store_id: localStorage.getItem('store_id')
    };

    const response = await post(`${process.env.EXTENDED_URL}/variant_option`, apiData);
    return variantOptionCreatePost(response);
  } catch (error) {
    console.error('Error creating variant option:', error);
    return {
      success: false,
      message: 'An error occurred while creating variant option',
      data: {}
    };
  }
};

/**
 * Update an existing variant option
 * @param {string} slack - Variant option slack
 * @param {Object} variantOptionData - Updated variant option data
 * @returns {Promise} Updated variant option response
 */
export const updateVariantOption = async (slack, variantOptionData) => {
  try {
    const apiData = {
      variant_option_name: variantOptionData.label,
      status: variantOptionData.status || 1,
      store_id: localStorage.getItem('store_id')
    };

    const response = await put(`${process.env.EXTENDED_URL}/variant_option/${slack}`, apiData);
    return variantOptionUpdatePut(response);
  } catch (error) {
    console.error('Error updating variant option:', error);
    return {
      success: false,
      message: 'An error occurred while updating variant option',
      data: {}
    };
  }
};

/**
 * Delete a variant option
 * @param {string} slack - Variant option slack
 * @returns {Promise} Delete response
 */
export const deleteVariantOption = async (slack) => {
  try {
    const response = await del(`${process.env.EXTENDED_URL}/variant_option/${slack}`);
    
    return variantOptionDeleteDel(response);
  } catch (error) {
    console.error('Error deleting variant option:', error);
    return {
      success: false,
      message: 'An error occurred while deleting variant option'
    };
  }
};

/**
 * Fetch variant options for dropdown
 * @returns {Promise} List of variant options for dropdown
 */
export const getVariantOptionsDropdown = async () => {
  try {
    const queryParams = {};

    const response = await get(`${process.env.EXTENDED_URL}/variant_option`, queryParams);
    
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || [],
        message: 'Variant options retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve variant options',
      data: []
    };
  } catch (error) {
    console.error('Error fetching variant options for dropdown:', error);
    return {
      success: false,
      message: 'An error occurred while fetching variant options',
      data: []
    };
  }
};

/**
 * Process variant option list response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const variantOptionListGet = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      // Match exact API structure with total and data array
      return {
        success: true,
        total: response.data.total || 0,
        data: response.data.data || [],
        message: response.data.message || 'Variant options retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve variant options',
      total: 0,
      data: []
    };
  } catch (error) {
    console.error('Error processing variant option list response:', error);
    return {
      success: false,
      message: 'Error processing variant option list response',
      total: 0,
      data: []
    };
  }
};

/**
 * Process variant option details response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const variantOptionDetailsGet = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Variant option details retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve variant option details',
      data: {}
    };
  } catch (error) {
    console.error('Error processing variant option details response:', error);
    return {
      success: false,
      message: 'Error processing variant option details response',
      data: {}
    };
  }
};

/**
 * Process variant option creation response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const variantOptionCreatePost = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Variant option created successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to create variant option',
      data: {}
    };
  } catch (error) {
    console.error('Error processing variant option creation response:', error);
    return {
      success: false,
      message: 'Error processing variant option creation response',
      data: {}
    };
  }
};

/**
 * Process variant option update response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const variantOptionUpdatePut = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Variant option updated successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to update variant option',
      data: {}
    };
  } catch (error) {
    console.error('Error processing variant option update response:', error);
    return {
      success: false,
      message: 'Error processing variant option update response',
      data: {}
    };
  }
};

/**
 * Process variant option delete response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const variantOptionDeleteDel = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        message: response.data.message || 'Variant option deleted successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to delete variant option'
    };
  } catch (error) {
    console.error('Error processing variant option delete response:', error);
    return {
      success: false,
      message: 'Error processing variant option delete response'
    };
  }
};
